import { Divider, Tag } from "antd";
import { useGetSingleJobPostQuery } from "@/app/features/recruitment/job-applicants/job-applicants.api";
import CustomDrawer from "@/components/CustomDrawer";
import type { CustomModalProps } from "@/components/CustomModal";
import StatusTag from "@/components/StatusTag";
import { formatDate } from "@/utils";
import WithLoader from "@/components/WithLoader";
import { cn } from "@/lib/cn";

type Props = CustomModalProps & { id?: string };

interface CVScoreItem {
  label: string;
  value: string | number | React.ReactNode;
}

interface RequirementItem {
  label: string;
  value: string | React.ReactNode;
}

const STATUS_CONFIG = [
  {
    label: "Published",
    value: "PUBLISHED",
    className: "status status-success",
  },
  { label: "Draft", value: "DRAFT", className: "status status-yellow" },
  { label: "Closed", value: "CLOSED", className: "status status-error" },
];

export default function JobPostDetails({ open, handleClose, id }: Props) {
  const { data, isLoading, isFetching } = useGetSingleJobPostQuery(id!, {
    skip: !id || !open,
  });

  const jobData = data?.data;

  const jobInfo = [
    { label: "Job Title", value: jobData?.job_title },
    { label: "Employee Type", value: jobData?.employee_type },
    {
      label: "Job Application Dead Line",
      value: jobData?.job_application_deadline,
    },
    { label: "Publish Date", value: formatDate(jobData?.publish_date) },
    {
      label: "Department",
      value: jobData?.department_name || jobData?.department_id,
    },
    {
      label: "Position",
      value: jobData?.position_name || jobData?.position_id,
    },
    { label: "Number Of vacancies", value: jobData?.vacancy_count },
    {
      label: "Assigned Evaluator",
      value: jobData?.evaluator_name || jobData?.evaluator_user_id,
    },
    { label: "Hiring Date", value: formatDate(jobData?.hiring_date) },
    { label: "Job Experience", value: jobData?.job_experience },
    { label: "Assign Grade", value: jobData?.grade_name || jobData?.grade_id },
    {
      label: "Salary Range",
      value:
        jobData?.salary_min_bdt && jobData?.salary_max_bdt
          ? `BDT ${jobData.salary_min_bdt} - ${jobData.salary_max_bdt}`
          : "N/A",
    },
  ];

  const cvScoreData: CVScoreItem[] = [
    {
      label: "CV Accepted Score (%)",
      value: `${jobData?.cv_accepted_score_percentage ?? 0}%`,
    },
    {
      label: "Interview Passing Rate(%)",
      value: `${jobData?.interview_passing_rate_percentage ?? 0}%`,
    },
    {
      label: "Skills",
      value: (
        <div className="flex flex-wrap gap-1">
          {jobData?.skills?.map((skill: string, index: number) => (
            <Tag key={index}>{skill}</Tag>
          ))}
        </div>
      ),
    },
    {
      label: "Functional Screening Key",
      value: (
        <div className="flex flex-wrap gap-1">
          {jobData?.functional_screening_keywords?.map(
            (item: string, index: number) => (
              <Tag key={index}>{item}</Tag>
            ),
          )}
        </div>
      ),
    },
    {
      label: "Tactical Key Weightage (%)",
      value: `${jobData?.tactical_keyword_weightage ?? 0}%`,
    },
    {
      label: "Prerequisite Screening Key",
      value: (
        <div className="flex flex-wrap gap-1">
          {jobData?.prerequisite_screening_keywords?.map(
            (item: string, index: number) => (
              <Tag key={index}>{item}</Tag>
            ),
          )}
        </div>
      ),
    },
    {
      label: "Prerequisite Key Weightage (%)",
      value: `${jobData?.prerequisite_keyword_weightage ?? 0}%`,
    },
    {
      label: "Functional Key Weightage (%)",
      value: `${jobData?.functional_keyword_weightage ?? 0}%`,
    },
  ];

  const requirementData: RequirementItem[] = [
    {
      label: "Job Context",
      value: (
        <div
          className="ql-snow ql-editor !p-0"
          dangerouslySetInnerHTML={{ __html: jobData?.job_context || "" }}
        />
      ),
    },
    {
      label: "Job Description",
      value: (
        <div
          className="ql-snow ql-editor !p-0"
          dangerouslySetInnerHTML={{ __html: jobData?.job_description || "" }}
        />
      ),
    },
    {
      label: "Educational Requirements",
      value: (
        <div
          className="ql-snow ql-editor !p-0"
          dangerouslySetInnerHTML={{
            __html: jobData?.educational_experience || "",
          }}
        />
      ),
    },
    {
      label: "Experience Requirements",
      value: (
        <div
          className="ql-snow ql-editor !p-0"
          dangerouslySetInnerHTML={{
            __html: jobData?.experience_requirement || "",
          }}
        />
      ),
    },
    {
      label: "Additional Requirements",
      value: (
        <div
          className="ql-snow ql-editor !p-0"
          dangerouslySetInnerHTML={{
            __html: jobData?.additional_requirement || "",
          }}
        />
      ),
    },
    {
      label: "Compensation Other Benefits",
      value: (
        <div
          className="ql-snow ql-editor !p-0"
          dangerouslySetInnerHTML={{
            __html: jobData?.compensation_and_benefit || "",
          }}
        />
      ),
    },
  ];

  const renderGridSection = (
    title: string,
    items: { label: string; value: any }[],
  ) => (
    <div className="mt-8">
      <div className="justify-start text-white text-xl font-bold">{title}</div>
      <Divider className="border-slate-600 mt-3 mb-0" />
      <div className="grid grid-cols-2 gap-x-8 gap-y-6 mt-5">
        {items.map((item, index) => (
          <div key={index}>
            <div className="justify-start text-white text-lg font-light mb-1">
              {item.label}
            </div>
            <div className="justify-start text-white text-base font-normal">
              {item.value || "N/A"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={jobData?.job_title || "Job Details"}
      titleStatusConfig={
        jobData?.status ? (
          <StatusTag value={jobData.status} config={STATUS_CONFIG} />
        ) : undefined
      }
    >
      <WithLoader
        isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}
      >
        <div>
          <div className="justify-start text-white text-xl font-bold">
            Basic Info :
          </div>
          <Divider className="border-slate-600 mt-3 mb-0" />

          <div className="grid grid-cols-2 gap-x-8 gap-y-6 mt-5">
            {jobInfo.map((item, index) => (
              <div key={index} className="flex flex-col gap-1">
                <div className="justify-start text-white text-lg font-light">
                  {item.label}
                </div>
                <div className="justify-start text-white text-base font-normal">
                  {item.value || "N/A"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {renderGridSection("CV Processing Score Distribution :", cvScoreData)}
        {renderGridSection("Other Requirement :", requirementData)}
      </WithLoader>
    </CustomDrawer>
  );
}
